"use client";
import { orderBy, groupBy, isEmpty, set } from "lodash";
import classNames from "classnames";
import styles from "./styles.module.scss";

import IconArrowChevronLeft from "@/components/Icon/IconArrowChevronLeft.svg";
import IconArrowChevronRight from "@/components/Icon/IconArrowChevronRight.svg";
import IconClose from "@/components/Icon/IconClose.svg";

import IconStarGray from "@/components/Icon/IconStarGray.svg";
import IconStarGold from "@/components/Icon/IconStarGold.svg";

import IconSearchZoomIn from "@/components/Icon/IconSearchZoomIn.svg";
import ColourSelect from "@/components/ProductDetails/ColourSelect";
import SizeSelector from "@/components/ProductDetails/SizeSelector";
import Availability from "@/components/ProductDetails/Availability";
import VehicleFitmentCheck from "@/components/ProductDetails/VehicleFitmentCheck";

import Image from "next/image";
import { Suspense, use, useEffect, useRef, useState } from "react";

import Link from "next/link";
import { useCart, useCheckout, useUserDetails } from "@/lib/saleor/reactSaleor";
import WishList from "@/components/ProductLayout/WishList";
import { productViewEvent } from "@/lib/gtm/dataLayerEvent";
import { pushAddToCartEvents } from "@/lib/gtm/events";
import { IMAGE_SIZES } from "@/constants";
import { KlaviyoEventKeys, createKlaviyoEvent, setAddToCartKlaviyoEvent } from "@/lib/klaviyo/events";
import dynamic from "next/dynamic";
const AddToCartDialog = dynamic(() => import("../AddToCartDialog"), { ssr: false });

const SizeGuideDialog = dynamic(() => import("@/components/ProductDetails/SizeGuideDialog"), {
  ssr: false,
});

export default function ProductDetail({
  product = {},
  slug,
  brand,
  department,
  vehicleNumber,
  reviewsPromise,
  bbaProductPromise,
  vehiclePromise,
  getSizeChartGuidePromise,
}) {
  const { addItem, items } = useCart();
  const { createCheckout } = useCheckout();
  const { data: userData } = useUserDetails({ fetchPolicy: "no-cache" });
  const { productColour, groupVariantsByColor, initVariants } = getColour(product);
  let currentImgIndex = product?.images?.findIndex(img => productColour[0] && img.url === productColour[0].mainImage);

  if (currentImgIndex === -1) {
    currentImgIndex = 0;
  }

  const [user, setUser] = useState(userData);
  const [inventories, setInventories] = useState([]);
  const [sizeVariants, setSizeVariants] = useState([]);
  const [currentVariantGroup, setCurrentVariantGroup] = useState(initVariants);
  const [selectedVariant, setSelectedVariant] = useState();
  const [selectedImgIndex, setCurrentImgIndex] = useState(currentImgIndex);
  const [showAddToCartDialog, setShowAddToCartDialog] = useState(false);
  const [showSelectVariantDialog, setShowSelectVariantDialog] = useState(false);
  const [showAddToCartStickyBar, setShowAddToCartStickyBar] = useState(false);
  const [showSizeGuideDialog, setShowSizeGuideDialog] = useState(false);
  const [cartDisabled, setCartDisabled] = useState(product?.virtualSku);
  const [manualZoom, setManualZoom] = useState(false);
  const handleChangeImage = item => {
    const imageIndex = product?.images?.findIndex(img => img.url === item.mainImage);
    if (imageIndex !== -1) {
      setCurrentImgIndex(imageIndex);
    }
  };

  const addToCartButtonRef = useRef(null);
  const handleScroll = () => {
    if (
      addToCartButtonRef.current.getBoundingClientRect().top <
      -addToCartButtonRef.current.getBoundingClientRect().height
    ) {
      setShowAddToCartStickyBar(true);
      if (document.getElementById("fc_frame")) {
        document.getElementById("fc_frame").style.bottom = "100px";
      }
    } else {
      setShowAddToCartStickyBar(false);
      if (document.getElementById("fc_frame")) {
        document.getElementById("fc_frame").style.bottom = "15px";
      }
    }
  };
  useEffect(() => {
    productViewEvent(product);

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [product]);

  useEffect(() => {
    setUser(userData);
  }, [userData]);

  useEffect(() => {
    const user = localStorage.getItem("user_metadata");
    const userEmail = JSON.parse(user)?.email;
    if (product?.virtualSku && product?.name) {
      createKlaviyoEvent(KlaviyoEventKeys.ViewProduct, userEmail, {
        sku: product.virtualSku,
        name: product.name,
      });
    }
  }, []);

  const handleAddToCart = async () => {
    setCartDisabled(true);
    if (!selectedVariant && !selectedVariant?.objectID && sizeVariants.length) {
      setShowSelectVariantDialog(true);
    } else {
      let selected;
      if (selectedVariant?.sku) {
        selected = product.variants?.find(variant => variant.sku === selectedVariant?.sku);
      } else {
        selected = product.variants[0];
        setSelectedVariant(selected);
      }
      let quantity = 1;
      const existedCardItem = items?.find(item => item.variant.sku === selected.sku);
      if (existedCardItem && existedCardItem.quantity === existedCardItem.variant.quantityAvailable) {
        quantity = 0;
      }

      //User logged in
      //If existing checkout, then update line checkout with selected variant
      //Otherwise, create new checkout with selected variant
      if (user) {
        if (user.checkout) {
          addItem(selected.id, quantity).then(() => {
            //Handle GTM add to cart
            pushAddToCartEvents([selected], product, quantity, "addToCart");

            setShowAddToCartDialog(true);
            setCartDisabled(false);
          });
        } else {
          //Create new checkout with selected variant & local cart
          const localCart = JSON.parse(localStorage.getItem("data_checkout") || "{}");
          const payload = {
            email: user.email,
            lines: [{ quantity, variantId: selected.id }],
          };
          if (!isEmpty(localCart) && localCart.lines.length && !localCart.token) {
            const linesPayload = localCart.lines.map(line => ({
              quantity: line.quantity,
              variantId: line.variant.id,
            }));
            payload.lines.push(...linesPayload);
          }

          // Trick `await` to wait for the statement completes
          await createCheckout(payload).then(({ data, dataError }) => {
            if (data) {
              setShowAddToCartDialog(true);
              setUser({ ...user, checkout: data });
              setCartDisabled(false);
            } else if (dataError) {
              console.error("Create checkout failed:", dataError);
              throw new Error(dataError);
            }
          });
        }
      } else {
        //Guest add item to cart
        addItem(selected.id, quantity).then(() => {
          setShowAddToCartDialog(true);
          setCartDisabled(false);

          //Handle GTM add to cart
          pushAddToCartEvents([selected], product, quantity, "addToCart");

          const localCart = JSON.parse(localStorage.getItem("data_checkout") || "{}");

          if (localCart?.lines?.length) {
            localStorage.setItem("guest_data_checkout", JSON.stringify(localCart));
          }
        });
      }
      setSelectedVariant(null);
      const userEmail = JSON.parse(localStorage.getItem("user_metadata"))?.email;
      setAddToCartKlaviyoEvent(userEmail ?? userData?.email, product, selectedVariant);
    }
  };
  return (
    <>
      <div id="productDetailSection" className="flex flex-wrap border-t-[1px] border-grey-200 font-roboto relative">
        <div className="w-full lg:w-8/12 pl-0 md:pl-8 lg:pl-[100px] py-6">
          <div className="flex pl-4 md:pl-0 font-roboto">
            <label className="text-sm font-bold">Brand:</label>

            <label className="text-sm text-gold underline ml-1">
              <Link href={`/brands/${brand?.slug}`}>{brand?.name} </Link>
            </label>
          </div>
          <div className="sticky top-0">
            <div className="w-full relative">
              {selectedImgIndex > 0 && (
                <Image
                  src={IconArrowChevronLeft.src}
                  alt=""
                  width={12}
                  height={12}
                  className="w-12 h-12 absolute left-0 md:left-6 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={() => setCurrentImgIndex(selectedImgIndex - 1)}
                />
              )}
              {product?.images && (
                <span className="hidden md:block text-sm absolute left-8 bottom-3">
                  {selectedImgIndex + 1}/{product?.images.length}
                </span>
              )}
              <Image
                priority
                className="w-[275px] md:w-[448px] h-[275px] md:h-[448px] m-auto object-contain"
                sizes="(max-width: 768px) 275px, 448px"
                src={product?.images && product?.images[selectedImgIndex] ? product?.images[selectedImgIndex].url : ""}
                width="448"
                height="448"
                alt={product?.images && product?.images[selectedImgIndex] ? product?.images[selectedImgIndex].alt : ""}
              />
              {product?.images && selectedImgIndex < product?.images.length - 1 && (
                <Image
                  src={IconArrowChevronRight.src}
                  alt=""
                  width={12}
                  height={12}
                  className="w-12 h-12 absolute right-0 md:right-6 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={() => setCurrentImgIndex(selectedImgIndex + 1)}
                />
              )}
              <div className="w-9 h-9 hidden md:flex absolute right-8 bottom-3 cursor-pointer border-[1px] border-black rounded-full">
                <Image
                  src={IconSearchZoomIn.src}
                  width="20"
                  height="20"
                  alt="search-zomm-in"
                  className="m-auto"
                  onClick={() => setManualZoom(true)}
                />
              </div>
            </div>
            <div className="flex md:hidden gap-2 justify-center">
              {product?.images &&
                product?.images.map((c, i) => (
                  <div
                    key={`${product?.name} - ${i}`}
                    className={classNames(
                      "w-2 h-2 cursor-pointer rounded-full",
                      selectedImgIndex === i ? "bg-black" : "bg-grey"
                    )}
                    onClick={() => setCurrentImgIndex(i)}
                  ></div>
                ))}
            </div>
            <div className="hidden md:flex gap-2 justify-center mt-4">
              {product?.images &&
                product?.images.map((c, i) => (
                  <div
                    key={`variant-${c.id}`}
                    className={classNames(
                      "border-[1px] cursor-pointer",
                      selectedImgIndex === i ? "border-gold" : "border-grey-200"
                    )}
                    onClick={() => setCurrentImgIndex(i)}
                  >
                    <Image
                      className="w-[62px] h-[62px] object-contain"
                      src={c.url}
                      width="62"
                      height="62"
                      alt={c.alt}
                    />
                  </div>
                ))}
            </div>
            {product.bestBuys === true && (
              <Image
                src="/best-buys-icon.png"
                alt="Bikebiz Best Buys"
                width={64}
                height={64}
                className="w-[64px] md:w-[121px] h-[64px] md:h-[121px] absolute top-0 right-8"
              />
            )}
          </div>
        </div>
        <div className="w-full lg:w-4/12 ml-auto bg-grey-50 pl-4 md:pl-8 pr-4 md:pr-8 lg:pr-[100px] pt-7 pb-7 lg:pb-[220px]">
          <p className="text-[26px] font-[500] leading-[26px] font-rajdhani">{product?.name}</p>
          <Link href="#reviews">
            <Suspense fallback={<div className="h-[26px] bg-grey-100 animate-pulse" />}>
              <ProductDetailReviews reviewsPromise={reviewsPromise} />
            </Suspense>
          </Link>
          <div className="flex mt-4 gap-2 font-rajdhani">
            {product?.pricing && product.pricing?.onSale && (
              <div className="flex text-red">
                <span className="text-[48px] font-[700] leading-[50px]">
                  &#36;{String(product.pricing?.priceRange?.start?.gross?.amount).split(".")[0]}
                </span>
                <span className="text-[21px] font-[700] leading-[36px] md:leading-[32px]">
                  .{String(product.pricing?.priceRange?.start?.gross?.amount.toFixed(2)).split(".")[1]}
                </span>
              </div>
            )}
            <div
              className={classNames("flex", {
                "text-grey text-[26px] leading-[26px] line-through": product?.pricing?.onSale,
              })}
            >
              {product.pricing?.onSale ? (
                <span className="font-[700] flex items-center leading-[60px]">
                  &#36;{product.pricing?.priceRangeUndiscounted?.start?.gross?.amount.toFixed(2)}
                </span>
              ) : (
                <div className="flex text-black font-rajdhani">
                  <span className="text-[31px] md:text-[48px] font-[700] leading-[31px] md:leading-[50px]">
                    &#36;{String(product.pricing?.priceRange?.start?.gross?.amount).split(".")[0]}
                  </span>
                  <span className="text-[18px] md:text-[21px] font-[700] leading-[24px] md:leading-[32px]">
                    .{String(product.pricing?.priceRange?.start?.gross?.amount.toFixed(2)).split(".")[1]}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="min-h-[65px] sm:min-h-[41px] lg:min-h-[89px] xl:min-h-[41px]">
            <div
              style={{ cursor: "pointer" }}
              id="zip-product-widget"
              data-zm-widget="popup"
              data-zm-region="au"
              data-env="production"
              data-zm-merchant={process.env.NEXT_PUBLIC_ZIP_KEY}
              data-zm-price={
                product?.pricing && product.pricing
                  ? product.pricing.onSale
                    ? product.pricing.priceRange?.start?.gross?.amount
                    : product.pricing.priceRangeUndiscounted?.start?.gross?.amount
                  : 0
              }
              data-zm-asset="productwidget"
              data-zm-popup-asset="termsdialog"
            ></div>
          </div>
          {department.name && department.name.toLowerCase() === "accessories" && (
            <Suspense fallback={<div className="h-[124px] bg-grey-100 animate-pulse" />}>
              <VehicleFitmentCheck
                vehicleNumber={vehicleNumber}
                bbaProductPromise={bbaProductPromise}
                vehiclePromise={vehiclePromise}
              />
            </Suspense>
          )}
          {productColour && productColour.length > 0 && (
            <ColourSelect
              colour={productColour}
              onSelectColor={item => {
                handleChangeImage(item);
                setCurrentVariantGroup(groupVariantsByColor[item.name] || []);
                setSelectedVariant(null);
              }}
              onHoverColor={item => {
                handleChangeImage(item);
              }}
              className="mt-2"
            />
          )}
          <SizeSelector
            variants={sizeVariants.filter(variant => currentVariantGroup.find(v => v.sku === variant.sku))}
            selectedVariant={selectedVariant}
            saleorVariants={currentVariantGroup}
            setShowSizeGuideDialog={data => setShowSizeGuideDialog(data)}
            setSelectedVariant={data => setSelectedVariant(data)}
            setInventories={data => setInventories(data)}
            className="mt-2"
            product={{ ...product, brand: brand }}
          />
          <div className="flex items-center mt-6 md:mt-8">
            <button
              ref={addToCartButtonRef}
              className={classNames("w-full h-[56px]  text-black font-bold rounded-sm text-lg", {
                "bg-gold": !cartDisabled,
                "bg-grey-200": cartDisabled,
              })}
              onClick={handleAddToCart}
              disabled={cartDisabled}
            >
              ADD TO CART
            </button>
            <div className="px-7 hidden md:block">
              <WishList
                width={20}
                height={20}
                rounded={false}
                product={{
                  name: product.name,
                  id: product.id,
                }}
              />
            </div>
          </div>
          <Suspense fallback={<div className="h-[273px] mt-8 bg-grey-100 animate-pulse" />}>
            <Availability
              sizeVariants={sizeVariants}
              saleorVariants={currentVariantGroup}
              variants={sizeVariants.filter(variant => currentVariantGroup.find(v => v.sku === variant.sku))}
              inventories={inventories && inventories.length > 0 ? inventories : selectedVariant?.inventories}
              selectedVariant={selectedVariant}
              product={product}
              setCartDisabled={setCartDisabled}
              setInventories={data => setInventories(data)}
              setSizeVariants={data => setSizeVariants(data)}
              bbaProductPromise={bbaProductPromise}
            />
          </Suspense>
          <div className="flex mt-8 gap-2 ">
            <span className="text-sm hidden md:block">We Accept: </span>
            <div className="flex flex-wrap gap-1">
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/visa.svg" width={30} height={10} alt="visa" />
              </div>
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/master-card.svg" width={23} height={14} alt="master-card" />
              </div>
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/amex.svg" width={38} height={10} alt="amex" />
              </div>

              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/paypal-pay.svg" width={38} height={13} alt="paypal-pay" />
              </div>
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/apple-pay.svg" width={34} height={16} alt="apple-pay" />
              </div>
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/after-pay.svg" width={40} height={7} alt="after-pay" />
              </div>
              <div className="w-[48px] h-[24px] flex items-center justify-center border-[1px] border-grey-200">
                <Image src="/zip-pay.svg" width={41} height={16} alt="zip-pay" />
              </div>
            </div>
          </div>
        </div>

        <div className={`dialog__slide ${showSelectVariantDialog ? "show" : ""}`}>
          <div className="dialog__content bottom">
            <div className={classNames(styles.addToCartDialog)}>
              {productColour && productColour.length > 0 && (
                <ColourSelect
                  colour={productColour}
                  onSelectColor={item => {
                    handleChangeImage(item);
                    setCurrentVariantGroup(groupVariantsByColor[item.name] || []);
                    setSelectedVariant(null);
                  }}
                  onHoverColor={item => {
                    handleChangeImage(item);
                  }}
                  className="mr-8"
                />
              )}
              <SizeSelector
                variants={sizeVariants.filter(variant => currentVariantGroup.find(v => v.sku === variant.sku))}
                selectedVariant={selectedVariant}
                saleorVariants={currentVariantGroup}
                setShowSizeGuideDialog={data => {
                  if (data) setShowSelectVariantDialog(false);
                  setShowSizeGuideDialog(data);
                }}
                setSelectedVariant={data => setSelectedVariant(data)}
                setInventories={data => setInventories(data)}
                className="mt-2"
                product={{ ...product, brand: brand }}
              />
              <button className="mt-auto mx-auto" onClick={() => setShowSelectVariantDialog(false)}>
                Cancel
              </button>
            </div>
          </div>
          <div className="dialog__overlay" onClick={() => setShowSelectVariantDialog(false)}></div>
        </div>

        <div
          className={`dialog__slide ${manualZoom ? "show" : ""}`}
          style={{ background: "hsla(0, 0%, 100%, .9) " }}
          onClick={() => setManualZoom(false)}
        >
          <div className="dialog__content center " style={{ width: "100%", height: "100%" }}>
            <div className="w-full h-full flex relative">
              <Image
                src={IconClose.src}
                alt="IconClose"
                width={24}
                height={24}
                className="cursor-pointer absolute top-10 right-10"
                onClick={() => handleShowSearchResult(false)}
              />
              <Image
                src={IconArrowChevronLeft.src}
                alt=""
                width={12}
                height={12}
                className="w-12 h-12 absolute left-0 md:left-6 top-1/2 -translate-y-1/2 cursor-pointer"
                onClick={e => {
                  e.stopPropagation();

                  setCurrentImgIndex(selectedImgIndex ? selectedImgIndex - 1 : product?.images.length - 1);
                }}
              />
              <Image
                className="h-[615px] w-auto m-auto object-contain"
                src={product?.images && product?.images[selectedImgIndex] ? product?.images[selectedImgIndex].url : ""}
                width="448"
                height="448"
                alt={product?.images && product?.images[selectedImgIndex] ? product?.images[selectedImgIndex].alt : ""}
              />
              <Image
                src={IconArrowChevronRight.src}
                alt=""
                width={12}
                height={12}
                className="w-12 h-12 absolute right-0 md:right-6 top-1/2 -translate-y-1/2 cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  setCurrentImgIndex(selectedImgIndex === product?.images.length - 1 ? 0 : selectedImgIndex + 1);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {showAddToCartStickyBar && (
        <div
          className="w-full h-[92px] bg-white fixed bottom-0 flex items-center px-[16px] md:px-8 lg:px-[100px]y font-rajdhani z-10"
          style={{ boxShadow: "0px -2px 8px 0px rgba(0, 0, 0, 0.25)" }}
        >
          <div className="hidden lg:flex gap-4 items-center mr-4 ml-auto">
            <p className="text-[21px] font-rajdhani font-[500]">{product?.name}</p>
            <Image
              src={product?.images && product?.images[selectedImgIndex] ? product?.images[selectedImgIndex].url : ""}
              width="56"
              height="56"
              alt="product"
              className="w-[74px] h-[74px] object-contain"
            />
          </div>
          <div className="w-full lg:w-fit flex items-center font-rajdhani">
            <div className="flex mt-4 gap-2 font-rajdhani">
              {product?.pricing && product.pricing.onSale && (
                <div className="flex text-red">
                  <span className="text-[31px] md:text-[48px] font-[700] leading-[31px] md:leading-[50px]">
                    &#36;{String(product.pricing?.priceRange?.start?.gross?.amount).split(".")[0]}
                  </span>
                  <span className="text-[21px] font-[700] leading-[36px] md:leading-[32px]">
                    .{String(product.pricing?.priceRange?.start?.gross?.amount.toFixed(2)).split(".")[1]}
                  </span>
                </div>
              )}
              <div
                className={classNames({
                  "hidden md:flex text-grey text-[26px] line-through": product?.pricing?.onSale,
                })}
              >
                {product.pricing?.onSale ? (
                  <span className="font-[700] flex items-center leading-[60px]">
                    &#36;{product.pricing?.priceRangeUndiscounted?.start?.gross?.amount.toFixed(2)}
                  </span>
                ) : (
                  <div className="flex text-black font-rajdhani">
                    <span className="text-[31px] md:text-[48px] font-[700] leading-[31px] md:leading-[50px]">
                      &#36;{String(product.pricing?.priceRange?.start?.gross?.amount).split(".")[0]}
                    </span>
                    <span className="text-[18px] md:text-[21px] font-[700] leading-[24px] md:leading-[32px]">
                      .{String(product.pricing?.priceRange?.start?.gross?.amount.toFixed(2)).split(".")[1]}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="ml-auto block md:hidden">
              <WishList
                width={20}
                height={20}
                rounded={false}
                product={{
                  name: product.name,
                  id: product.id,
                }}
              />
            </div>
            <button
              className={classNames(
                "h-[48px] w-[171px] md:w-[244px] lg:w-[286px] text-center text-lg font-bold rounded ml-4 md:ml-auto lg:ml-[32px]",
                {
                  "bg-gold": !cartDisabled,
                  "bg-grey-200": cartDisabled,
                }
              )}
              onClick={handleAddToCart}
              disabled={cartDisabled}
            >
              ADD TO CART
            </button>
          </div>
        </div>
      )}
      <SizeGuideDialog
        open={showSizeGuideDialog}
        onChange={data => setShowSizeGuideDialog(data)}
        getSizeChartGuidePromise={getSizeChartGuidePromise}
      />
      {showAddToCartDialog && (
        <AddToCartDialog open={showAddToCartDialog} onChange={data => setShowAddToCartDialog(data)} />
      )}
    </>
  );
}

const getColour = product => {
  if (!product) return {};
  let productColour = [];
  if (product?.descriptionJson) {
    const colourDescription = JSON.parse(product?.descriptionJson);
    productColour = orderBy(colourDescription.colour, "order");
  }
  const groupVariantsByColor = groupBy(product?.variants, variant => {
    const colorAttribute = variant.attributes.find(attr => attr.attribute.name === "Colour");
    if (colorAttribute) {
      return colorAttribute.values[0]?.name;
    }
  });
  const defaultColor = productColour[0]?.name || Object.keys(groupVariantsByColor)[0];
  const initVariants =
    defaultColor && groupVariantsByColor[defaultColor] ? groupVariantsByColor[defaultColor] : product?.variants;

  return { productColour, groupVariantsByColor, initVariants };
};

const ProductDetailReviews = ({ reviewsPromise }) => {
  const reviewsData = use(reviewsPromise);
  return (
    <div className="flex gap-2 mt-1 items-center">
      <div className="flex gap-1">
        {[...Array(5)].map((_, index) =>
          index < reviewsData?.rating && reviewsData?.total > 0 ? (
            <Image key={`star-${index}`} src={IconStarGold.src} alt="" width={14} height={14} />
          ) : (
            <Image key={`star-${index}`} src={IconStarGray.src} alt="" width={14} height={14} />
          )
        )}
      </div>
      <p className={classNames("text-xs font-[500] leading-[22px] text-grey font-roboto")}>
        ({reviewsData?.total} reviews)
      </p>
    </div>
  );
};
